import { z } from "zod";

import { codecGet, dualKey, isObject } from "@/api/codec";

/**
 * ``GET /api/version/`` — which build is the WEB container running?
 *
 * The public half of ``server/views.py::build_version``: identity only
 * (channel, sha, build time, and which stamp answered). The build id, the
 * loaded settings module and the image digests live on the container-internal
 * ``/version`` mount, which nginx does not proxy — a browser cannot reach it.
 *
 * Pairs with ``src/lib/buildInfo.ts``, which reports the sha baked into THIS
 * bundle. ``frontend-build`` and ``web`` are separate images from the same
 * build, so the two shas can disagree; that disagreement is the whole reason
 * both are read. See ``components/common/BuildStamp.tsx``.
 *
 * Rides ``codecGet`` (``rawResponse: true``) like every other read fetcher —
 * the global axios camelizer is skipped and this schema owns the wire shape.
 * ``dualKey`` reads the BE's snake_case first with a camel fall-back, so a
 * payload that ever rode the legacy camelizer parses identically.
 */

export interface BuildVersion {
  /** Which stamp answered: ``image`` | ``deploy`` | ``none`` | ``error``. */
  source: string;
  /** ``dev`` | ``prod`` | ``unknown`` — what the stamp CLAIMS to be. */
  channel: string;
  gitSha: string;
  gitShaShort: string;
  builtAt: string;
}

/**
 * Every field is a plain string and the BE guarantees all five are present
 * (missing values arrive as the literal ``"unknown"``, never null). The
 * schema still defaults each one: a version badge must degrade, never throw,
 * and ``codecGet`` turns a schema miss into a ``CodecParseError``.
 */
const buildVersionSchema = z.unknown().transform((raw): BuildVersion => {
  const obj = isObject(raw) ? raw : {};
  const read = (key: string): string => {
    const value = dualKey(obj, key);
    return typeof value === "string" && value.trim() ? value.trim() : "unknown";
  };
  return {
    source: read("source"),
    channel: read("channel"),
    gitSha: read("git_sha"),
    gitShaShort: read("git_sha_short"),
    builtAt: read("built_at"),
  };
});

export const fetchBuildVersion = (): Promise<BuildVersion> =>
  codecGet("version/", buildVersionSchema);
